import React, { useState, useMemo } from "react";

import TextareaAutosize from "react-autosize-textarea";

import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import { animated, useTransition } from "@react-spring/web";

import { Podcast } from "src/types/coaching/podcast";
import Reveal from "src/components/animation";
import { TabBar } from "src/components/tabbar";
import { BannerView } from "src/components/banner";

import _ from "lodash";
import cx from "classnames";
import styles from "./details.module.scss";
import { PodcastMiniPlayer } from "../player";
import { PodcastEditState } from "../studio";
import { EditCourseThumbnail } from "../../../courses/edit/thumbnail";
import { PodcastTranscriptRowPlaceholder } from "src/pages/coaching/components/podcast/components/transcripts";
import { FileService } from "src/utils/file.service";

interface PodcastDetailsProps {
  podcast: Podcast;
  editState: PodcastEditState;
  // eslint-disable-next-line no-unused-vars
  onChange: (property: string, value: any) => void;
}

export const PodcastDetails: React.FC<PodcastDetailsProps> = ({ podcast, editState, onChange }) => {
  const [activeTab, setActiveTab] = useState(0);

  const handleSelectionChange = (action: number) => {
    setActiveTab(action);
  };

  const showAudio = useMemo(() => {
    const audio = _.get(podcast, "audio");
    if (!audio) {
      return false;
    }
    const needsProcessing = _.get(podcast, "audioProcessing.status") === 'pending';
    if (needsProcessing) {
      return false;
    }

    return true;
  }, [podcast]);

  return (
    <section className={styles.container}>
      <span className={styles.header}>
        here&apos;s a customized topics and transcripts for you!
      </span>

      {showAudio &&
        <PodcastMiniPlayer podcast={podcast} />
      }
      <PodcastDetailCard podcast={podcast} editState={editState} onChange={onChange} />
      <div className={styles.tabContainer}>
        <TabBar
          className={cx(styles.tabs)}
          gap={32}
          tabSize="auto"
          active={activeTab}
          onSelectionChange={(action) => handleSelectionChange(action)}
        >
          <span className={cx(styles.tab, { [styles.active]: activeTab === 0 })}>
            Topics covered
          </span>
          <span className={cx(styles.tab, { [styles.active]: activeTab === 1 })}>
            Transcript
          </span>
        </TabBar>

        <div className={cx(styles.stacks, styles.tabContentContainer)}>
          <TabSection index={0} selected={activeTab}>
            <div className={styles.topics}>
              {(podcast?.topics || []).map((topic, index) => (
                <div key={index} className={styles.topic}>
                  <div className={styles.name}>{topic.name}</div>
                </div>
              ))}
            </div>
          </TabSection>

          <TabSection index={1} selected={activeTab}>
            <PodcastTranscriptSection podcast={podcast} />
          </TabSection>
        </div>
      </div>
    </section>
  );
};
interface PodcastDetailCardProps {
  podcast: Podcast;
  editState: PodcastEditState;
  // eslint-disable-next-line no-unused-vars
  onChange: (property: string, value: any) => void;
}

const PodcastDetailCard: React.FC<PodcastDetailCardProps> = ({ podcast, editState, onChange }) => {
  const nameInputRef = React.useRef<any>(null);

  const dispatch = useDispatch();
  const [editThumbnail, setEditThumbnail] = useState(false);

  const name = useMemo(() => {
    return _.get(editState, "name") || "";
  }, [editState]);

  const nameError = useSelector((state: any) => {
    const errors = _.get(state.coaching.podcasts, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "name" });
  });

  const thumbnailError = useSelector((state: any) => {
    const errors = _.get(state.coaching.podcasts, `edit.errors`) || [];
    return _.find(errors, { type: "course", property: "banner" });
  });

  const handleNameChange = (e: any) => {
    let input = e.target.value;
    input = input.replace(/\n/g, "");
    onChange("name", input);
  };

  const handleThumbnailChange = (banner: any) => {
    onChange("banner", banner);
    setEditThumbnail(false);
  };

  const duration = useMemo(() => {
    const value = _.get(podcast, "audio.duration", 0);
    if (value > 0) {
      return FileService.formatDuration(value);
    }
    return "n/a";
  }, [podcast]);

  return (
    <div className={styles.detailsCard}>
      <div className={styles.summaryRow}>
        <div className={cx(styles.field, styles.thumbnail)}>
          <div className={styles.inner}>
            <div className={styles.overlay} onClick={() => setEditThumbnail(true)}>
              <i className="paperflite-edit"></i>
            </div>
            <BannerView
              banner={editState?.banner as any}
              defaultBanner="/images/assets/podcastThumbnail.jpg"
              overlay={false}
            />
          </div>
          <Reveal show={thumbnailError != null}>
            <div className={styles.error}>
              <i className="fa-solid fa-triangle-exclamation"></i>
              <span>{thumbnailError?.message}</span>
            </div>
          </Reveal>
        </div>
        <div className={cx(styles.field, styles.name)}>
          <TextareaAutosize
            className={styles.input}
            ref={nameInputRef}
            placeholder="Enter Podcast name..."
            rows={1}
            maxRows={10}
            value={name}
            data-testid="podcast-name"
            onChange={handleNameChange}
            onPointerEnterCapture={() => { }}
            onPointerLeaveCapture={() => { }}
          />

          <Reveal show={nameError != null}>
            <div className={styles.error}>
              <i className="fa-solid fa-triangle-exclamation"></i>
              <span>{nameError?.message}</span>
            </div>
          </Reveal>
        </div>
      </div>

      <div className={cx(styles.field, styles.configurations)}>
        <div className={styles.item}>
          <i className={cx(styles.duration, "paperflite-clock")} />
          <span>{duration}</span>
        </div>
        <div className={styles.item}>
          <span>Type:</span>
          <span>{podcast.type}</span>
        </div>
      </div>

      {editThumbnail && (
        <EditCourseThumbnail
          show={editThumbnail}
          type='podcast'
          banner={editState?.banner || null}
          defaultBanner="/images/assets/podcastThumbnail.jpg"
          onFinish={handleThumbnailChange}
          onClose={() => setEditThumbnail(false)}
        />
      )}
    </div>
  );
};

interface TabSectionProps {
  selected: number;
  index: number;
  children: React.ReactNode;
}

const TabSection: React.FC<TabSectionProps> = ({
  index,
  selected,
  children,
}) => {
  const active = index === selected;


  const tabAnimation = useTransition(active, {
    from: { opacity: 0, transform: `translate3d(0,${100}%, 0)` },
    enter: { opacity: 1, transform: `translate3d(0,0,0)` },
    leave: { opacity: 0, transform: `translate3d(0,${100}%, 0)` },
    config: { tension: 300, friction: 30 },
  });

  return <>{tabAnimation((style, item) =>
    item && (
      <animated.div style={style} className={cx(styles.tabBody, styles.stack)}>
        {children}
      </animated.div>
    ))}</>;
};


interface PodcastTranscriptSectionProps {
  podcast: Podcast;
}

const PodcastTranscriptSection: React.FC<PodcastTranscriptSectionProps> = ({ podcast }) => {
  const dispatch = useDispatch();

  const { loading, transcripts } = useSelector((state: any) => {
    const loading = _.get(state.coaching.podcasts, `edit.editorSession.transcript.loading`) || false;
    const transcripts = _.get(state.coaching.podcasts, `edit.editorSession.transcript.item`, null);

    return { loading, transcripts };
  });

  const podcastType = useMemo(() => {
    return _.get(podcast, "type");
  }, [podcast]);

  return (
    <div className={styles.transcripts}>

      {loading && (
        _.times(5, (index) => (
          <PodcastTranscriptRowPlaceholder key={index} />
        ))
      )}

      {(!loading && transcripts?.topicSections || []).map((transcript) => {
        return (transcript?.conversations || []).map(
          (conversation, idx) => {

            let speaker: string | null = null;
            if (podcastType === "conversational") {
              if(conversation.role == 'speaker 1'){
                speaker = 'Person 1';
              }else if(conversation.role == 'speaker 2'){
                speaker = 'Person 2';
              }
            }

            return (
              <div key={idx} className={styles.conversation}>
                {speaker &&
                  <div className={styles.role}>
                    {speaker}
                    <button className={styles.play}>
                      <i className={'paperflite-play'} />
                    </button>
                  </div>
                }
                <div className={styles.text}>{conversation.text}</div>
              </div>
            );
          },
        );
      })}
    </div>
  );
}